import type { ResumeEngagement } from '../components/core/JobComponent/EmployerOutreach/ResumeEngagementDataBuilder';

import { mapToOutreachInfo } from './mapToOutreachInfo';
import { useEmployerOutreachSource } from './useEmployerOutreachSource';
import { type OutreachInfo, OutreachStatus } from './useEmployerOutreachUIConfig';
import { useFullScreenVJActive } from './useFullScreenVJActive';
import { useResumeEngagement } from './useResumeEngagement';
import { useViewjobProctorAllocation } from '../../proctor/__generated__/ViewjobProctorContext';
import {
    RnEmployerOutreachTst,
    RnViewjobOnegraphTst,
} from '../../proctor/__generated__/ViewjobProctorSpecifications';
import { useConfirmInterestContext } from '../context/EmployerOutreachContext';
import { useResumeEngagementContextData } from '../context/ViewJobDataContext';

import { Platform } from 'react-native';


export interface EmployerOutreachResult {
    outreachInfo: OutreachInfo;
    mostRecentEngagement: ResumeEngagement;
}

/**
 * Hook to get employer outreach info combining engagement data and URL params
 * Returns both the structured OutreachInfo and the raw most recent engagement
 */
export const useEmployerOutreachInfo = (jobKey: string): EmployerOutreachResult | null => {
    const isEmployerOutreachActive = useViewjobProctorAllocation(RnEmployerOutreachTst).value > 1 || Platform.OS === 'web';
    const isViewjobOneGraphActive = useViewjobProctorAllocation(RnViewjobOnegraphTst).value > 1;
    const isFullScreenVJActive = useFullScreenVJActive();

    const resumeEngagementContext = useResumeEngagementContextData();
    const { isConfirmed = false } = useConfirmInterestContext() ?? {};
    const { data, loading, error } = useResumeEngagement(
        jobKey,
        isViewjobOneGraphActive,
        isEmployerOutreachActive,
        isFullScreenVJActive,
        resumeEngagementContext,
    );

    const employerOutreachData = useEmployerOutreachSource();

    if (
        !(isEmployerOutreachActive || isFullScreenVJActive) ||
        loading ||
        error ||
        !data?.engagements?.length
    ) {
        return null;
    }

    const { engagements } = data;
    const mostRecentEngagement =
        engagements.length > 0
            ? engagements.reduce((latest, current) =>
                  new Date(current.createdDate) > new Date(latest.createdDate) ? current : latest,
              )
            : null;

    if (!mostRecentEngagement) {
        return null;
    }

    const outreachInfo = mapToOutreachInfo(mostRecentEngagement, employerOutreachData);

    if (!outreachInfo) {
        return null;
    }

    if (isConfirmed && outreachInfo.outreachStatus === OutreachStatus.INITIATED) {
        return {
            outreachInfo: {
                ...outreachInfo,
                outreachStatus: OutreachStatus.INTERESTED,
            },
            mostRecentEngagement,
        };
    }

    return {
        outreachInfo,
        mostRecentEngagement,
    };
};
